import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePilgrimGuideStore } from '../../stores/pilgrimGuideStore';
import { ErrorMessage } from '../common/ErrorMessage';
import { PilgrimGuideTable } from './table/PilgrimGuideTable';
import { useLanguage } from '../../contexts/LanguageContext';
import { Loader2 } from 'lucide-react';
import { LoadingProgress } from '../common/LoadingProgress';

export const PilgrimGuideList = () => {
  const { 
    links, 
    error, 
    initialized,
    loading,
    progress,
    fetchLinks,
  } = usePilgrimGuideStore();
  const { translations } = useLanguage();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedOwnership, setSelectedOwnership] = useState('all');

  // Initialize data
  useEffect(() => {
    if (!initialized) {
      fetchLinks();
    }
  }, [initialized, fetchLinks]);

  // Memoized filtered links
  const filteredLinks = useMemo(() => {
    return Object.values(links).filter(link => {
      const matchesSearch = !searchTerm || 
        link.pilgrimName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(link.passportNumber).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (link.email && link.email.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus = !selectedStatus || link.status === selectedStatus;
      const matchesCountry = !selectedCountry || link.residenceCountry === selectedCountry;
      
      // Add ownership filter
      const matchesOwnership = 
        selectedOwnership === 'all' ||
        (selectedOwnership === 'owner' && link.isOwner !== false) ||
        (selectedOwnership === 'notOwner' && link.isOwner === false);

      return matchesSearch && matchesStatus && matchesCountry && matchesOwnership;
    });
  }, [links, searchTerm, selectedStatus, selectedCountry, selectedOwnership]);

  if (error) return <ErrorMessage message={error} onRetry={fetchLinks} />;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-lg shadow-sm"
      >
        {/* Loading Progress */}
        {(loading || !initialized) && progress.total > 0 && (
          <div className="p-4 border-b">
            <LoadingProgress
              progress={progress.current}
              total={progress.total}
              label={translations?.pilgrimGuideList?.loadingPilgrims || 'Loading pilgrims...'}
            />
          </div>
        )}

        <PilgrimGuideTable 
          links={filteredLinks}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          selectedCountry={selectedCountry}
          onCountryChange={setSelectedCountry}
          selectedOwnership={selectedOwnership}
          onOwnershipChange={setSelectedOwnership}
        />

        {/* Bottom section with loading indicator or empty state */}
        <div className="p-4 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {filteredLinks.length} {translations?.pagination?.pilgrims || 'pilgrims'}
            </span>
            {loading && (
              <div className="flex items-center gap-2 text-primary-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">
                  {translations?.loading?.loadingData || 'Loading data...'}
                </span>
              </div>
            )}
          </div>
        </div>

        {filteredLinks.length === 0 && !loading && initialized && (
          <div className="p-8 text-center text-gray-500">
            {translations?.pilgrimGuideList?.noPilgrims ||
              'No linked pilgrims currently.'}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};